import ActivityReview from './ActivityReview';
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import {
  Cpu, HardDrive, Wifi, Monitor, MemoryStick, Server,
  Package, Settings, AlertTriangle, Activity, Clock,
  Search, ChevronLeft, ChevronRight, Loader2, XCircle,
  CheckCircle, Info, Shield, RefreshCw,
} from 'lucide-react';

function FreshnessTag({ timestamp }) {
  if (!timestamp) return <span className="text-xs text-gray-400">Never synced</span>;
  const age = (Date.now() - new Date(timestamp).getTime()) / 60000;
  const label = age < 60 ? `${Math.round(age)}m ago` : age < 1440 ? `${Math.round(age / 60)}h ago` : `${Math.round(age / 1440)}d ago`;
  const color = age < 120 ? 'text-green-600' : age < 2880 ? 'text-amber-600' : 'text-red-600';
  return <span className={`text-xs ${color}`}><Clock className="h-3 w-3 inline mr-1" />{label}</span>;
}

function ErrorState({ message }) {
  return <div className="text-center py-8 text-gray-500"><XCircle className="h-6 w-6 mx-auto mb-2 text-red-400" />{message}</div>;
}

function LoadingState() {
  return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
}

// ── Hardware Tab ──────────────────────────────────────────────────────

export function RmmHardwareTab({ assetId }) {
  const [hw, setHw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/rmm/asset/${assetId}/hardware`).then(r => setHw(r.data)).catch(e => setError('Hardware data unavailable')).finally(() => setLoading(false));
  }, [assetId]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!hw || hw.message) return <ErrorState message={hw?.message || 'No hardware data'} />;

  return (
    <div className="space-y-4" data-testid="rmm-hardware-tab">
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Hardware Inventory</h3>
        <FreshnessTag timestamp={hw.last_synced_at} />
      </div>

      {/* System */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Monitor className="h-4 w-4" /> System</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><span className="text-gray-500">Manufacturer</span><p className="font-medium">{hw.manufacturer || '—'}</p></div>
          <div><span className="text-gray-500">Model</span><p className="font-medium">{hw.model || '—'}</p></div>
          <div><span className="text-gray-500">Serial</span><p className="font-mono text-xs">{hw.serial_number || '—'}</p></div>
          <div><span className="text-gray-500">Memory</span><p className="font-medium">{hw.total_memory_gb ? `${hw.total_memory_gb} GB` : '—'}</p></div>
        </CardContent>
      </Card>

      {/* Processors */}
      {hw.processors?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Cpu className="h-4 w-4" /> Processors</CardTitle></CardHeader>
          <CardContent>
            {hw.processors.map((p, i) => (
              <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                <span>{p.name || 'Unknown CPU'}</span>
                <span className="text-gray-500">{p.cores ? `${p.cores} cores` : ''} {p.logical_processors ? `/ ${p.logical_processors} threads` : ''}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Disks */}
      {hw.disks?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><HardDrive className="h-4 w-4" /> Storage</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Drive</TableHead><TableHead>Capacity</TableHead><TableHead>Free</TableHead><TableHead>Used</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {hw.disks.map((d, i) => {
                  const used = d.capacity_gb > 0 && d.free_gb != null ? Math.round(((d.capacity_gb - d.free_gb) / d.capacity_gb) * 100) : null;
                  return (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm">{d.drive_letter || d.name || `Disk ${i}`}</TableCell>
                      <TableCell>{d.capacity_gb ? `${d.capacity_gb} GB` : '—'}</TableCell>
                      <TableCell>{d.free_gb != null ? `${d.free_gb} GB` : '—'}</TableCell>
                      <TableCell>{used !== null ? <Badge variant="custom" className={used > 90 ? 'bg-red-100 text-red-700' : used > 75 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{used}%</Badge> : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Network */}
      {hw.network_interfaces?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Wifi className="h-4 w-4" /> Network</CardTitle></CardHeader>
          <CardContent>
            {hw.network_interfaces.map((n, i) => (
              <div key={i} className="py-2 border-b last:border-0 text-sm">
                <div className="font-medium">{n.name || 'Unknown Adapter'}</div>
                <div className="text-gray-500 text-xs">
                  {n.mac_address && <span className="mr-3">MAC: {n.mac_address}</span>}
                  {n.ip_addresses?.length > 0 && <span>IP: {n.ip_addresses.join(', ')}</span>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Software Tab ─────────────────────────────────────────────────────

export function RmmSoftwareTab({ assetId }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showRemoved, setShowRemoved] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [uninstallTarget, setUninstallTarget] = useState(null);
  const [uninstallReason, setUninstallReason] = useState('');
  const [uninstallLoading, setUninstallLoading] = useState(false);
  const [activeActions, setActiveActions] = useState({});

  const load = (p = 1, q = search) => {
    setLoading(true);
    setError(null);
    api.get(`/rmm/asset/${assetId}/software`, { params: { page: p, page_size: pageSize, search: q, installed_only: !showRemoved } })
      .then(r => { setData(r.data); setPage(p); })
      .catch(() => setError('Software data unavailable'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [assetId, showRemoved, pageSize]);

  const classifySoftware = (name, publisher) => {
    const n = (name || '').toLowerCase();
    const protectedPatterns = ['ninjaone', 'ninjarmm', 'ninja rmm', 'bitdefender', 'gravityzone',
      'crowdstrike', 'sentinelone', 'microsoft defender', 'windows defender',
      'veeam', 'acronis', 'datto', 'shadow protect'];
    const sensitivePatterns = ['screenconnect', 'connectwise'];
    for (const p of protectedPatterns) if (n.includes(p)) return 'PROTECTED';
    for (const p of sensitivePatterns) if (n.includes(p)) return 'SENSITIVE';
    return 'NORMAL';
  };

  const handleUninstall = async () => {
    if (!uninstallTarget || uninstallReason.trim().length < 5) return;
    setUninstallLoading(true);
    try {
      const res = await api.post('/rmm/actions/software-uninstall', {
        asset_id: assetId,
        software_identity: uninstallTarget.software_identity,
        reason: uninstallReason.trim(),
      });
      const detail = res.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (res.data.action_id) {
        setActiveActions(prev => ({ ...prev, [uninstallTarget.software_identity]: res.data }));
        toast.success(`Uninstall action created: ${res.data.status}`);
      } else if (res.data.status === 'existing_action') {
        toast.info('An active uninstall action already exists for this package');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Uninstall request failed');
    }
    setUninstallTarget(null);
    setUninstallReason('');
    setUninstallLoading(false);
  };

  const protectionBadge = (cls) => {
    if (cls === 'PROTECTED' || cls === 'SYSTEM_PROTECTED') return <Badge variant="custom" className="bg-red-100 text-red-700"><Shield className="h-3 w-3 mr-1" />Protected</Badge>;
    if (cls === 'SENSITIVE') return <Badge variant="custom" className="bg-amber-100 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" />Sensitive</Badge>;
    return null;
  };

  const actionStatusBadge = (action) => {
    if (!action) return null;
    const s = action.status || '';
    const map = {
      REQUESTED: 'bg-blue-100 text-blue-700', AWAITING_APPROVAL: 'bg-amber-100 text-amber-700',
      EXECUTING: 'bg-blue-100 text-blue-700', PROVIDER_ACCEPTED: 'bg-blue-100 text-blue-700',
      VERIFYING: 'bg-blue-100 text-blue-700', SUCCEEDED: 'bg-green-100 text-green-700',
      FAILED: 'bg-red-100 text-red-700', UNVERIFIED: 'bg-gray-100 text-gray-600',
    };
    const labels = {
      PROVIDER_ACCEPTED: 'Accepted by NinjaOne', VERIFYING: 'Verifying removal',
      SUCCEEDED: 'Removed', UNVERIFIED: 'Unable to verify',
    };
    return <Badge variant="custom" className={map[s] || 'bg-gray-100 text-gray-500'}>{labels[s] || s}</Badge>;
  };

  return (
    <div className="space-y-3" data-testid="rmm-software-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search software..." value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(1, search)} className="pl-8" />
        </div>
        <Button variant={showRemoved ? 'default' : 'outline'} size="sm" onClick={() => setShowRemoved(!showRemoved)}>
          {showRemoved ? 'All' : 'Installed'}
        </Button>
        <label className="flex items-center gap-2 text-sm">Rows <select aria-label="Software rows per page" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>{[10,25,50].map(n => <option key={n} value={n}>{n}</option>)}</select></label>
        <span className="text-sm text-gray-500">{data.total} packages</span>
      </div>

      {/* Uninstall Confirmation Dialog */}
      {uninstallTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setUninstallTarget(null)}>
          <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-5 w-5 text-red-500" /> Confirm Software Uninstall
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
                <div><span className="text-gray-500">Software:</span> <span className="font-medium">{uninstallTarget.name}</span></div>
                <div><span className="text-gray-500">Version:</span> {uninstallTarget.version || '—'}</div>
                <div><span className="text-gray-500">Publisher:</span> {uninstallTarget.publisher || '—'}</div>
              </div>
              <div>
                <label className="text-sm font-medium">Reason for removal *</label>
                <Input value={uninstallReason} onChange={e => setUninstallReason(e.target.value)}
                  placeholder="e.g., Remove deprecated Java runtime" maxLength={500} data-testid="uninstall-reason" />
                {uninstallReason.trim().length > 0 && uninstallReason.trim().length < 5 &&
                  <p className="text-xs text-red-500 mt-1">Reason must be at least 5 characters</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={handleUninstall}
                  disabled={uninstallLoading || uninstallReason.trim().length < 5}
                  className="flex-1" data-testid="confirm-uninstall">
                  {uninstallLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Uninstall
                </Button>
                <Button variant="outline" onClick={() => setUninstallTarget(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {error ? <ErrorState message={error} /> : loading ? <LoadingState /> : (
        <>
          <Table className="table-fixed">
            <TableHeader><TableRow>
              <TableHead className="w-[35%]">Name</TableHead><TableHead>Version</TableHead><TableHead>Publisher</TableHead><TableHead>Status</TableHead><TableHead className="w-24"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.items.map((s, i) => {
                const cls = classifySoftware(s.name, s.publisher);
                const action = activeActions[s.software_identity];
                return (
                  <TableRow key={i}>
                    <TableCell>
                      <span className="font-medium text-sm">{s.name}</span>
                      {protectionBadge(cls) && <span className="ml-2">{protectionBadge(cls)}</span>}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">{s.version || '—'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{s.publisher || '—'}</TableCell>
                    <TableCell>
                      {action ? actionStatusBadge(action) : (
                        s.currently_installed ?
                          <Badge variant="custom" className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Installed</Badge> :
                          <Badge variant="custom" className="bg-gray-100 text-gray-500">Removed</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.currently_installed && cls === 'NORMAL' && !action && (
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 text-xs"
                          onClick={() => setUninstallTarget(s)} data-testid={`uninstall-${i}`}>
                          Uninstall
                        </Button>
                      )}
                      {s.currently_installed && cls === 'SENSITIVE' && !action && (
                        <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 text-xs"
                          onClick={() => setUninstallTarget(s)} data-testid={`uninstall-${i}`}>
                          Uninstall
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {data.items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-400">No software found</TableCell></TableRow>}
            </TableBody>
          </Table>
          {data.pages > 1 && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" aria-label="Previous software page" disabled={page <= 1 || loading} onClick={() => load(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-gray-500 py-1">Page {page} of {data.pages}</span>
              <Button variant="outline" size="sm" aria-label="Next software page" disabled={page >= data.pages || loading} onClick={() => load(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Services Tab ─────────────────────────────────────────────────────

export function RmmServicesTab({ assetId }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);

  const load = (p = 1) => {
    setLoading(true);
    api.get(`/rmm/asset/${assetId}/services`, { params: { page: p, page_size: 50, search, state: stateFilter } })
      .then(r => { setData(r.data); setPage(p); })
      .catch(() => setError('Service data unavailable'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [assetId, stateFilter]);

  const stateColor = (s) => {
    if (!s) return 'bg-gray-100 text-gray-500';
    const sl = s.toLowerCase();
    if (sl.includes('running')) return 'bg-green-100 text-green-700';
    if (sl.includes('stopped')) return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="space-y-3" data-testid="rmm-services-tab">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search services..." value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load(1)} className="pl-8" />
        </div>
        {['', 'Running', 'Stopped'].map(s => (
          <Button key={s} variant={stateFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStateFilter(s)}>
            {s || 'All'}
          </Button>
        ))}
        <span className="text-sm text-gray-500">{data.total} services</span>
      </div>

      {error ? <ErrorState message={error} /> : loading ? <LoadingState /> : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Service</TableHead><TableHead>Display Name</TableHead><TableHead>State</TableHead><TableHead>Startup</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((s, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{s.service_name}</TableCell>
                <TableCell className="text-sm">{s.display_name || '—'}</TableCell>
                <TableCell><Badge variant="custom" className={stateColor(s.state)}>{s.state || '—'}</Badge></TableCell>
                <TableCell className="text-sm text-gray-500">{s.start_type || '—'}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-400">No services found</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Alerts Tab ───────────────────────────────────────────────────────

export function RmmAlertsTab({ assetId }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/rmm/asset/${assetId}/alerts`, { params: { page_size: 50 } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [assetId]);

  if (loading) return <LoadingState />;

  return (
    <div data-testid="rmm-alerts-tab">
      {data.items.length === 0 ? (
        <div className="text-center py-8 text-gray-400"><CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-400" />No recent alerts</div>
      ) : (
        <div className="space-y-2">
          {data.items.map((a, i) => (
            <Card key={i} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${a.processing_status === 'failed' ? 'text-red-500' : 'text-amber-500'}`} />
                  <span className="text-sm font-medium">{a.event_type || 'Event'}</span>
                </div>
                <span className="text-xs text-gray-400">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
              </div>
              {a.ticket_id && <span className="text-xs text-blue-600">Ticket: {a.ticket_id}</span>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Activities Tab ───────────────────────────────────────────────────

export function RmmActivitiesTab({ assetId }) {
  const [data,setData]=useState({items:[],total:0});
  const [loading,setLoading]=useState(true),[error,setError]=useState('');
  const [page,setPage]=useState(1),[refresh,setRefresh]=useState(0);
  useEffect(()=>{setPage(1);setData({items:[],total:0});},[assetId]);
  useEffect(()=>{let active=true;setLoading(true);setError('');
    api.get('/rmm/asset/'+encodeURIComponent(assetId)+'/activities',{params:{page,page_size:50}})
      .then(r=>{if(active)setData(r.data);})
      .catch(e=>{if(active){setData({items:[],total:0});setError(typeof e.response?.data?.detail==='string'?e.response.data.detail:'Activity could not be loaded. Refresh to retry.');}})
      .finally(()=>{if(active)setLoading(false);});
    return()=>{active=false;};
  },[assetId,page,refresh]);
  return <div data-testid="rmm-activities-tab" className="space-y-4">
    <Button variant="outline" disabled={loading} onClick={()=>setRefresh(n=>n+1)}>Refresh activities</Button>
    {error&&<p role="alert" className="text-destructive">{error}</p>}
    {loading?<LoadingState/>:!error&&<ActivityReview key={assetId+'-'+page} activities={data.items}/>}
    {data.total>50&&<div className="flex flex-wrap items-center gap-3"><Button variant="outline" disabled={loading||page<=1} onClick={()=>setPage(n=>n-1)}>Newer records</Button><span className="text-sm">Record batch {page} of {Math.ceil(data.total/50)}</span><Button variant="outline" disabled={loading||page*50>=data.total} onClick={()=>setPage(n=>n+1)}>Older records</Button></div>}
  </div>;
}

// ── Patches Tab ──────────────────────────────────────────────────────

export function RmmPatchesTab({ assetId }) {
  const [patches, setPatches] = useState({ items: [], total: 0, os_pending: 0, os_failed: 0, sw_pending: 0, sw_failed: 0 });
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('current'); // current, history
  const [category, setCategory] = useState('');
  const [applyTarget, setApplyTarget] = useState(null);
  const [applyReason, setApplyReason] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);

  const handlePatchApply = async () => {
    if (!applyTarget || applyReason.trim().length < 5) return;
    setApplyLoading(true);
    try {
      await api.post('/rmm/actions', {
        action_type: 'PATCH_APPLY', asset_id: assetId,
        parameters: { patch_category: applyTarget, reason: applyReason.trim() },
      });
      toast.success(`${applyTarget} Patch Apply started`);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Patch apply failed');
    }
    setApplyTarget(null);
    setApplyReason('');
    setApplyLoading(false);
  };


  useEffect(() => {
    setLoading(true);
    api.get(`/rmm/asset/${assetId}/patches`, { params: { category } })
      .then(r => setPatches(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [assetId, category]);

  const loadHistory = (cat = 'OS') => {
    api.get(`/rmm/asset/${assetId}/patches/history`, { params: { category: cat, page_size: 50 } })
      .then(r => setHistory(r.data))
      .catch(() => {});
  };

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab]);

  const statusColor = (s) => {
    const map = { PENDING: 'bg-amber-100 text-amber-700', INSTALLED: 'bg-green-100 text-green-700',
                  FAILED: 'bg-red-100 text-red-700', REJECTED: 'bg-gray-100 text-gray-500' };
    return map[s] || 'bg-gray-100 text-gray-500';
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-3" data-testid="rmm-patches-tab">
      <div className="flex items-center gap-2">
        <Button variant={tab === 'current' ? 'default' : 'outline'} size="sm" onClick={() => setTab('current')}>Current</Button>
        <Button variant={tab === 'history' ? 'default' : 'outline'} size="sm" onClick={() => setTab('history')}>History</Button>
        <div className="flex-1" />
        {tab === 'current' && (
          <>
            {['', 'OS', 'SOFTWARE'].map(c => (
              <Button key={c} variant={category === c ? 'default' : 'outline'} size="sm" onClick={() => setCategory(c)}>
                {c || 'All'}
              </Button>
            ))}
          </>
        )}
      </div>

      {/* Summary */}
      {tab === 'current' && (
        <div className="grid grid-cols-4 gap-2 text-center text-sm">
          <Card className="p-2"><div className="font-bold text-amber-600">{patches.os_pending}</div><div className="text-xs text-gray-500">OS Pending</div></Card>
          <Card className="p-2"><div className="font-bold text-red-600">{patches.os_failed}</div><div className="text-xs text-gray-500">OS Failed</div></Card>
          <Card className="p-2"><div className="font-bold text-amber-600">{patches.sw_pending}</div><div className="text-xs text-gray-500">SW Pending</div></Card>
          <Card className="p-2"><div className="font-bold text-red-600">{patches.sw_failed}</div><div className="text-xs text-gray-500">SW Failed</div></Card>
        </div>
      )}

      {/* Patch list */}
      {tab === 'current' ? (
        patches.items.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            {patches.error ? <><XCircle className="h-5 w-5 mx-auto mb-1 text-red-400" />{patches.error}</> :
              <><CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-400" />No outstanding patches</>}
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Patch</TableHead><TableHead>KB/ID</TableHead><TableHead>Category</TableHead>
              <TableHead>Severity</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {patches.items.map((p, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm font-medium">{p.name}</TableCell>
                  <TableCell className="text-xs font-mono text-gray-500">{p.kb_number || '—'}</TableCell>
                  <TableCell><Badge variant="custom" className="bg-blue-100 text-blue-700 text-xs">{p.category}</Badge></TableCell>
                  <TableCell className="text-xs">{p.severity || '—'}</TableCell>
                  <TableCell><Badge variant="custom" className={statusColor(p.status)}>{p.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      ) : (
        /* History */
        history.items.length === 0 ? (
          <div className="text-center py-6 text-gray-400">No patch installation history</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Patch</TableHead><TableHead>KB/ID</TableHead><TableHead>Status</TableHead><TableHead>Installed</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.items.map((h, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm">{h.name}</TableCell>
                  <TableCell className="text-xs font-mono text-gray-500">{h.kb_number || '—'}</TableCell>
                  <TableCell><Badge variant="custom" className={statusColor(h.status)}>{h.status}</Badge></TableCell>
                  <TableCell className="text-xs text-gray-500">{h.installed_at ? new Date(h.installed_at).toLocaleString() : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      )}

      <p className="text-xs text-gray-400 text-center mt-2">
        Patch operations are device-scoped. Reboot behavior is controlled by the NinjaOne patch policy and may occur.
      </p>

      {/* Apply Confirmation Dialog */}
      {applyTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setApplyTarget(null)}>
          <Card className="w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="text-base">Run {applyTarget} Patch Apply</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800">
                ⚠️ This is a device-scoped operation. Reboot behavior is controlled by the NinjaOne patch policy and may occur.
              </div>
              <div className="text-sm space-y-1">
                <div>Category: <strong>{applyTarget}</strong></div>
                <div>Pending: <strong>{applyTarget === 'OS' ? patches.os_pending : patches.sw_pending}</strong></div>
                <div>Failed: <strong>{applyTarget === 'OS' ? patches.os_failed : patches.sw_failed}</strong></div>
              </div>
              <div>
                <label className="text-sm font-medium">Reason *</label>
                <Input value={applyReason} onChange={e => setApplyReason(e.target.value)}
                  placeholder="e.g., Apply approved updates during maintenance" maxLength={500} />
                {applyReason.trim().length > 0 && applyReason.trim().length < 5 &&
                  <p className="text-xs text-red-500 mt-1">Min 5 characters</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={handlePatchApply}
                  disabled={applyLoading || applyReason.trim().length < 5} className="flex-1">
                  {applyLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Run {applyTarget} Patch Apply
                </Button>
                <Button variant="outline" onClick={() => setApplyTarget(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Controls */}
      <div className="flex flex-wrap gap-2 justify-center pt-2 border-t">
        <Button variant="outline" size="sm" onClick={() => {
          api.post(`/rmm/asset/${assetId}/patches/refresh`).then(() => {
            toast.success('Patch data refreshed');
            setLoading(true);
            api.get(`/rmm/asset/${assetId}/patches`).then(r => setPatches(r.data)).finally(() => setLoading(false));
          }).catch(e => toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Refresh failed'));
        }} data-testid="patch-refresh">
          <RefreshCw className="h-3 w-3 mr-1" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => {
          api.post('/rmm/actions', { action_type: 'PATCH_SCAN', asset_id: assetId, parameters: { patch_category: 'OS' } })
            .then(() => toast.success('OS Patch Scan started'))
            .catch(e => toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Scan failed'));
        }} data-testid="os-scan">Run OS Scan</Button>
        <Button variant="outline" size="sm" onClick={() => {
          api.post('/rmm/actions', { action_type: 'PATCH_SCAN', asset_id: assetId, parameters: { patch_category: 'SOFTWARE' } })
            .then(() => toast.success('Software Patch Scan started'))
            .catch(e => toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Scan failed'));
        }} data-testid="sw-scan">Run SW Scan</Button>
        <Button variant="default" size="sm" onClick={() => setApplyTarget('OS')} data-testid="os-apply">
          Run OS Patch Apply
        </Button>
        <Button variant="default" size="sm" onClick={() => setApplyTarget('SOFTWARE')} data-testid="sw-apply">
          Run SW Patch Apply
        </Button>
      </div>
    </div>
  );
}
